import { useState, PropsWithChildren } from "react";
import {
  Box,
  Collapse,
  CollapseHeader,
  CollapsePanel,
  Heading,
  Stack,
  Text,
} from "@vinomofo/components";

import { TextBlockProps } from "../../text-block/TextBlock";
import { richTextRenderer } from "../../../utils/rich-text-renderer";
import styled from "styled-components";
import { ArrowDownIcon, ArrowUpIcon } from "@vinomofo/icons";

const StyledCollapse = styled(Collapse)`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: rgb(19, 19, 20);
  }
`;

const StyledAccordionpanel = styled(CollapsePanel)`
  p:not(:last-child) {
    @media ${({ theme }) => theme.mq.max.desktop} {
      padding-bottom: 12px;
    }
    @media ${({ theme }) => theme.mq.max.tablet} {
      padding-bottom: 8px;
    }
  }
`;

export interface AccordionProps {
  title?: string;
  shortDescription?: string;
  collections: Array<TextBlockProps>;
}

export type TextAlignT = "left" | "right" | "center";

export const AccordionPanel = ({
  title,
  textAlign,
  children,
}: PropsWithChildren<{
  title: string;
  textAlign: TextAlignT;
}>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <StyledCollapse isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
      <CollapseHeader
        icon={<ArrowDownIcon />}
        closeIcon={<ArrowUpIcon />}
        backgroundColor="almostWhite"
        padding="20px"
        iconSize="20px"
        justifyContent={
          textAlign?.toLocaleLowerCase() === "center"
            ? textAlign.toLocaleLowerCase()
            : "space-between"
        }
        flexDirection={
          textAlign?.toLocaleLowerCase() === "right" ? "row-reverse" : "row"
        }
      >
        <Box mr={textAlign?.toLocaleLowerCase() === "right" ? "0" : "20px"}>
          <Heading as="h2" variant="xxs" textAlign="left">
            {title}
          </Heading>
        </Box>
      </CollapseHeader>
      <StyledAccordionpanel
        backgroundColor="almostWhite"
        padding="0 20px 20px"
        textAlign={textAlign ? textAlign?.toLocaleLowerCase() : "left"}
      >
        {children}
      </StyledAccordionpanel>
    </StyledCollapse>
  );
};

export const Accordion = ({
  title,
  shortDescription,
  collections,
}: AccordionProps): JSX.Element => {
  if (collections.length === 0) return null;

  return (
    <Stack space={3}>
      {title && (
        <Heading as="h2" variant="xl" textAlign="center">
          {title}
        </Heading>
      )}
      {shortDescription && (
        <Text as="p" textAlign="center">
          {shortDescription}
        </Text>
      )}
      <Stack space={2}>
        {collections &&
          collections.map((i, index) => (
            <AccordionPanel
              key={index}
              title={i.header}
              textAlign={i.textAlign}
            >
              {richTextRenderer(i.bodyText, {
                p: { fontSize: [1, 2], lineHeight: 1.8 },
              })}
            </AccordionPanel>
          ))}
      </Stack>
    </Stack>
  );
};
